import {
  Cluster,
  ClusterStats,
  Marker,
  Renderer,
  MarkerClusterer,
  SuperClusterViewportAlgorithm,
} from '@googlemaps/markerclusterer';
import { StoreHandoffService } from 'src/app/services/store-handoff.service';
import { StringToColor } from 'src/app/common/stringToColor';
import { conditionallyLog } from 'src/app/common/consoleLogHelper';
import { ToastrService } from 'ngx-toastr';
import { FacilityManager } from 'src/app/managers/facility.manager';

export class ZipCodeOverlayManager {
  private labelMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
  private clusterMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
  private listeners: google.maps.MapsEventListener[] = [];

  private isInitialized = false;
  private lastVisibleClusterKeys = new Set<string>();
  private zipcodeLabelsEnabled = true;
  private readonly currentVisibleClusterKeys = new Set<string>();
  private readonly clusterer!: MarkerClusterer;

  constructor(
    private readonly map: google.maps.Map,
    private readonly storeHandoffService: StoreHandoffService,
    private readonly toastr: ToastrService,
    private readonly facilityManager: FacilityManager,
  ) {
    this.clusterer = new MarkerClusterer({
      map: this.map,
      markers: [],
      algorithm: new SuperClusterViewportAlgorithm({ radius: 100 }),
      renderer: this.renderer,
    });

    this.map.addListener('idle', () => {
      this.lastVisibleClusterKeys = new Set(this.currentVisibleClusterKeys);
      this.currentVisibleClusterKeys.clear();
    });

    this.map.addListener('zoom_changed', () => {
      this.lastVisibleClusterKeys.clear();
      this.clusterMarkers = [];
    });
  }

  public toggleOn(): void {
    if (!this.isInitialized) {
      this.initialize();
    }
  }

  public toggleOff(): void {
    if (this.isInitialized) {
      this.remove();
      this.zipcodeLabelsEnabled = true; // reset back to default
    }
  }

  public toggleLabels(enabled: boolean): void {
    this.zipcodeLabelsEnabled = enabled;
    const animate = (element: HTMLElement): void => {
      element.style.animationFillMode = 'forwards';
      if (enabled) {
        element.style.animation = 'popIn 0.3s ease-out';
        element.style.display = 'flex';
      } else {
        element.style.animation = 'popOut 0.3s ease-in';
        element.style.display = 'none';
      }
    };

    this.labelMarkers.forEach((marker) => {
      animate(marker.content as HTMLDivElement);
    });

    this.clusterMarkers.forEach((marker) => {
      animate(marker.content as HTMLDivElement);
    });
  }

  private initialize(): void {
    const facilityId = this.facilityManager.getCurrentFacility();
    this.storeHandoffService.GetDeliveryZipCodes(facilityId).subscribe({
      next: (zipCodes) => {
        conditionallyLog('zip codes received', zipCodes);

        this.listeners.push(
          this.map.data.addListener('addfeature', (event: any) => {
            this.addZipcodeLabel(event.feature);
          }),
        );

        this.map.data.addGeoJson(zipCodes);
        this.clusterer.addMarkers(this.labelMarkers);

        this.map.data.setStyle((feature) => ({
          fillColor: StringToColor.stringToColor(feature.getProperty('ZCTA5CE20') as string),
          strokeColor: 'black',
          strokeWeight: 1,
          fillOpacity: 0.15,
          clickable: false,
        }));

        this.isInitialized = true;
      },
      error: (err) => {
        conditionallyLog('error fetching zip codes', err);
        this.toastr.error('Error fetching delivery zip codes');
      },
    });
  }

  private addZipcodeLabel(feature: any): void {
    const zipCode = feature.getProperty('GEOID20') || feature.getProperty('ZCTA5CE20');
    const labelLat = parseFloat(feature.getProperty('INTPTLAT20'));
    const labelLng = parseFloat(feature.getProperty('INTPTLON20'));

    if (!zipCode || isNaN(labelLat) || isNaN(labelLng)) return;

    const labelLatLng = new google.maps.LatLng(labelLat, labelLng);

    const zipcodeLabelElement = document.createElement('div');
    zipcodeLabelElement.className = 'zip-code-label';
    zipcodeLabelElement.textContent = zipCode;
    zipcodeLabelElement.style.animation = 'popIn 0.3s ease-in-out';

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: labelLatLng,
      content: zipcodeLabelElement,
      title: zipCode,
    });

    this.labelMarkers.push(marker);
  }

  private remove(): void {
    conditionallyLog('removing zip code overlay');

    this.map.data.forEach((feature) => this.map.data.remove(feature));

    // removes the listeners so if we toggle it back on, we don't add duplicate listeners + cleanup memory
    this.listeners.forEach((l) => l.remove());
    this.listeners = [];

    // remove all of the labels and clusters + reset the state
    this.clusterer.clearMarkers();
    this.labelMarkers = [];
    this.clusterMarkers = [];

    this.isInitialized = false;
  }

  private readonly renderer: Renderer = {
    render: ({ count, position }: Cluster, stats: ClusterStats, map: google.maps.Map): Marker => {
      const key = `${count}-${position.lat()}-${position.lng()}`;
      this.currentVisibleClusterKeys.add(key);

      // Create the circle
      const svg = window.btoa(`
        <svg fill="black" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
          <circle cx="120" cy="120" opacity="0.8" r="70" />
        </svg>`);
      const svgUrl = `data:image/svg+xml;base64,${svg}`;
      const img = document.createElement('img');
      img.src = svgUrl;
      img.classList.add('zip-code-svg-img');

      // Create the text label
      const label = document.createElement('div');
      label.textContent = String(count);
      label.classList.add('zip-code-label-text');

      // Create the div to hold the image and label. This div will control the animation and visibility.
      const markerContent = document.createElement('div');
      markerContent.classList.add('zip-code-cluster-marker');
      markerContent.appendChild(img);
      markerContent.appendChild(label);

      // we still need to create the marker even if labels are disabled so that the clusterer can manage it
      // but we need to hide the content if labels are disabled. This allows the user to enable again later
      if (!this.zipcodeLabelsEnabled) {
        markerContent.style.display = 'none';
      }

      // Only animate if we haven't seen this marker key before.
      // This prevents flashing when moving the map around.
      const shouldAnimate = !this.lastVisibleClusterKeys.has(key);
      if (shouldAnimate) {
        markerContent.style.animation = 'popIn 0.3s ease-out';
        markerContent.style.animationFillMode = 'forwards';
      }

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position,
        content: markerContent,
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
      });

      this.clusterMarkers.push(marker);
      return marker;
    },
  };
}
